import { createGlobalStyle } from "styled-components"

import { media, mixins } from "./globalTheme"

import ArrowSVG from "../images/icons/arrow-right.svg"

const GlobalStyle = createGlobalStyle`
  :root {
    --font-size-10: 0.625rem;
    --font-size-12: 0.75rem;
    --font-size-13: 0.8125rem;
    --font-size-14: 0.875rem;
    --font-size-15: 0.9375rem;
    --font-size-16: 1rem;
    --font-size-17: 1.0625rem;
    --font-size-18: 1.125rem;
    --font-size-20: 1.25rem;
    --font-size-21: 1.3125rem;
    --font-size-24: 1.5rem;
    --font-size-32: 2rem;
    --font-size-48: 3rem;
    --font-size-72: 4.5rem;

    --color-blueberry: ${({ theme }) => theme.blueberryRGB};
    --color-blueberryRGB: ${({ theme }) => theme.blueberryRGB};
    --color-ebony: ${({ theme }) => theme.ebony};
    --color-error: ${({ theme }) => theme.error};
    --color-greyLight: ${({ theme }) => theme.greyLight};
    --color-greyLightRGB: ${({ theme }) => theme.greyLightRGB};
    --color-greyLightAlt: ${({ theme }) => theme.greyLightAlt};
    --color-greyWarm: ${({ theme }) => theme.greyWarm};
    --color-greyWarmRGB: ${({ theme }) => theme.greyWarmRGB};
    --color-greyWarmAlt: ${({ theme }) => theme.greyWarmAlt};
    --color-rose: ${({ theme }) => theme.rose};
    --color-sage: ${({ theme }) => theme.sage};
    --color-sageRGB: ${({ theme }) => theme.sageRGB};
    --color-sky: ${({ theme }) => theme.sky};
    --color-spaceblue: ${({ theme }) => theme.spaceblue};
    --color-spaceblueRGB: ${({ theme }) => theme.spaceblueRGB};
    --color-success: ${({ theme }) => theme.success};
    --color-sun: ${({ theme }) => theme.sun};
    --color-white: ${({ theme }) => theme.white};
    --color-whiteRGB: ${({ theme }) => theme.whiteRGB};
    --transitionTime: ${({ theme }) => theme.transitionTime};

    --header-height: 50px;
    --header-height-lg: 80px;

    --swiper-pagination-bullet-horizontal-gap: 5px;
    --swiper-theme-color: var(--color-spaceblue) !important;
    --swiper-pagination-bullet-inactive-color: var(--color-spaceblue);
    --swiper-pagination-bullet-size: 10px;
  }

  * {
    box-sizing: border-box;
  }

  html {
    /* scroll-behavior: smooth; */
    scroll-padding-top: var(--header-height);
    @media ${media.lg} {
      scroll-padding-top: var(--header-height-lg);
    }
  }

  body {
    color: var(--color-spaceblue);
    font-family: "Scto Grotesk A", Arial, Helvetica, sans-serif;
    margin: 0;
    -moz-osx-font-smoothing: grayscale; /* better firefox font rendering */
    -webkit-font-smoothing: antialiased; /* better webkit font rendering */

    @media print {
      background: white;
      color: black;
      font-size: 12pt;
    }
  }

  body.scroll-locked {
    height: 100%;
    overflow: hidden;
  }

  .site {
    display: flex;
    flex-direction: column;
    min-height: 100vh;

    @media print {
      display: block;
      min-height: 0;
    }
  }

  .site-content {
    flex-grow: 1;
    padding-top: var(--header-height);

    @media ${media.md} {
      padding-top: calc(var(--header-height) + 10px);
    }

    @media ${media.lg} {
      padding-top: calc(var(--header-height-lg) + 50px);
    }

    @media ${media.xl} {
      padding-top: calc(var(--header-height-lg) + 55px);
    }
  }

  .site-content--compact {
    padding-top: calc(var(--header-height) + 1px);

    @media ${media.md} {
      padding-top: calc(var(--header-height) + 11px);
    }

    @media ${media.lg} {
      padding-top: calc(var(--header-height-lg) + 1px);
    }

    @media ${media.xl} {
      padding-top: calc(var(--header-height-lg) + 1px);
    }
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0 0 20px;
  }

  a {
    color: var(--color-sun);

    &.arrow::after {
      background-color: var(--color-sun);
      content: "";
      display: inline-block;
      height: 14px;
      margin-left: 8px;
      mask: ${`url(${ArrowSVG}) no-repeat 50% 50%`};
      mask-size: cover;
      width: 14px;
    }
  }

  p {
    margin: 0 0 1em;
  }

  :is(a, button, input, textarea, summary, [role="button"], [tabindex]):focus {
    outline-offset: 5px;
  }

  :is(a, button, input, textarea, summary, [role="button"], [tabindex]):focus-visible {
    outline-offset: 5px;
  }

  :is(a, button, input, textarea, summary, [role="button"], [tabindex]):focus:not(:focus-visible) {
    outline: none;
  }

  [data-reach-dialog-overlay] {
    background: rgba(var(--color-greyLightRGB), 0.75);
    z-index: 10;
  }

  [data-reach-dialog-content] {
    max-width: 1020px;
    padding: 15px;
    width: 90vw;
  }

  .highlightText {
    background: var(--color-rose);
    color: var(--color-white);
  }

  @media (prefers-reduced-motion: reduce) {
    .gatsby-image-wrapper img {
      transition: none !important;
    }
  }

  .privacy-settings {
    background: var(--color-greyLight);
    border-radius: 25px;
    margin: 0 0 3rem;
    padding: 30px;

    p {
      margin: 0 0 1.5rem;
    }

    p:last-child {
      margin: 0;
    }

    .button {
      background-color: var(--color-spaceblue);
      align-items: center;
      border: 2px solid var(--color-spaceblue);
      border-radius: 30px;
      color: var(--color-white);
      display: inline-flex;
      font-size: var(--font-size-18);
      line-height: 1;
      min-height: 58px;
      padding: 5px 30px;
      text-decoration: none;
      transition: background-color var(--transitionTime),
        border-color var(--transitionTime),
        opacity var(--transitionTime);

      @media (prefers-reduced-motion: reduce) {
        transition: none;
      }

      &:hover {
        background-color: var(--color-sun);
        border-color: var(--color-sun);
        text-decoration: none;
      }
    }
  }


  #ppms_cm_popup {
    * {
      box-sizing: border-box !important;
      font-family: "Scto Grotesk A", Arial, Helvetica, sans-serif !important;
    }

    h1.ppms_cm_header1 {
      ${mixins.title4}
      letter-spacing: -0.5px !important;
    }

    button.ppms_cm_button_primary,
    button.ppms_cm_button {
      border-radius: 50px !important;
      min-height: 58px !important;
      padding: 5px 30px !important;
      transition: background-color var(--transitionTime) !important;

      @media (prefers-reduced-motion: reduce) {
        transition: none !important;
      }

      @media ${media.mdDown} {
        font-size: var(--font-size-16px) !important;
        height: auto !important;
        padding: 10px 4px !important;
      }
    }
  }

  // Make sure site header is still visible on mobile
  #ppms_cm_popup_wrapper {
    @media ${media.smDown} {
      height: calc(100% - 50px) !important;
      top: 50px !important;
      position: relative !important;
    }
  }

  // Add gradient to consent list to indicate that it is scrollable
  /* #ppms_cm_popup #ppms_cm_popup_responsive_wrapper_id + .ppms_cm_centered_buttons {
    position: relative !important;

    &:before {
      background: linear-gradient(
        to bottom,
        rgba(var(--color-whiteRGB), 0),
        rgba(var(--color-whiteRGB), 1)
      );
      height: 15px;
      content: " ";
      width: 100%;
      top: -15px;
      position: absolute;
    }
  } */
`

export default GlobalStyle
